// @flow
import * as React from 'react';
import { useState } from 'react';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';
import moment from 'moment';
import { keycloak } from '@coverright/shared/keycloak';
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { Client } from '../../../../shared';
import { ModalCloseButton } from '@coverright/ui/shared';
import { LoadingButton } from '@mui/lab';
import StatusFilter from '../../../../features/status-filter';
import { ExpectedCloseDateSelector, FollowUpSelector } from '../../../../features';
import { SelectInput } from '@coverright/ui/inputs';
import { useSaveNextContactDateDialog } from './hooks/use-save-next-contact-date';
import { DisqualifiedReasonDto, StatusDto } from '@coverright/data-access/types/enrollment';


export const ExpiredContactDateDialog = ({ client }: { client: Partial<Client> }) => {
  const shouldBlock = client && client.status && [StatusDto.FollowUp, StatusDto.SalesQualified, StatusDto.Contacted].includes(client.status) &&
    client.followUpDate && moment(client.followUpDate).isSameOrBefore(moment().endOf('day')) &&
    keycloak.tokenParsed?.sub === client.agent?.id;

  const [wasClosed, setWasClosed] = useState(false);

  const [status, setStatus] = useState<StatusDto | ''>(client.status || '');
  const [showStatusSelector, setShowStatusSelector] = useState<boolean>();
  const [followUpDate, setFollowUpDate] = React.useState<string>(moment().add(1, 'day').format('MM/DD/YYYY'));
  const [expectedCloseDate, setExpectedCloseDate] = React.useState<string>('');
  const [disqualifiedReason, setDisqualifiedReason] = React.useState<DisqualifiedReasonDto | ''>('');
  const {save, loading} = useSaveNextContactDateDialog(client)

  const blocker = useBlocker(shouldBlock && !wasClosed)

  const open = shouldBlock && !wasClosed && blocker.state === 'blocked';

  const handleSaveClick = async () => {
    if (blocker?.state === 'blocked') {
      await save({
        status: status || undefined,
        disqualifiedReason: disqualifiedReason || undefined,
        expectedCloseDate,
        followUpDate,
        tagAsMissed: showStatusSelector === false
      })
      blocker && blocker.proceed();
    }
  };

  const isDateValid = status === StatusDto.Disqualified || !!followUpDate && moment(followUpDate, 'MM/DD/YYYY').isSameOrAfter(moment());
  const expectedCloseDateValid = status !== StatusDto.SalesQualified || !!expectedCloseDate && moment(expectedCloseDate).isSameOrAfter(moment());
  const disableSaveButton = !isDateValid || !expectedCloseDateValid || (status === StatusDto.FollowUp && !showStatusSelector) || (showStatusSelector && !status);


  if (!client) {
    return null;
  }

  const reset = () => {
    setStatus('');
    setFollowUpDate(moment().add(1, 'day').format('MM/DD/YYYY'));
    setDisqualifiedReason('');
  };

  return (
    <Dialog fullWidth maxWidth={'sm'} open={open || false} onClose={() => setWasClosed(true)}>
      <DialogTitle variant={'h3'}>
        Next contact date for this client has expired.
        <ModalCloseButton onClick={() => setWasClosed(true)} sx={{ position: 'absolute', top: 4, right: 8 }} />
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ py: 2 }}>
          <Stack gap={3} direction={'row'} alignItems={'center'}>
            <Typography>Did you speak with this customer?</Typography>

            <ButtonGroup>
              <Button onClick={() => {
                setShowStatusSelector(false);
                reset();
              }} size={'small'}
                      variant={showStatusSelector === false ? 'contained' : 'outlined'}>No</Button>
              <Button onClick={() => {
                setShowStatusSelector(true);
                reset();
              }} size={'small'}
                      variant={showStatusSelector ? 'contained' : 'outlined'}>Yes</Button>
            </ButtonGroup>
          </Stack>

          <Collapse in={showStatusSelector === false}>
            <Typography sx={{ mt: 2 }}><i>This customer will be tagged as 'missed appointment'</i></Typography>
          </Collapse>

          <Collapse in={showStatusSelector}>
            <Box sx={{ mt: 2 }}>
              <StatusFilter label={'Status'}
                            statuses={[StatusDto.SalesQualified, StatusDto.Contacted, StatusDto.Disqualified]}
                            onChange={(value) => setStatus(value as StatusDto)}
                            value={status} />
            </Box>
          </Collapse>

          <Collapse in={status === StatusDto.Contacted || status === StatusDto.SalesQualified}>
            <Box sx={{ mt: 2 }}>
              <FollowUpSelector value={followUpDate} onChange={v => setFollowUpDate(v || '')} />
            </Box>
          </Collapse>

          <Collapse in={status === StatusDto.SalesQualified}>
            <Box sx={{ mt: 2 }}>
              <ExpectedCloseDateSelector value={expectedCloseDate} onChange={v => setExpectedCloseDate(v || '')} />
            </Box>
          </Collapse>

          <Collapse in={status === StatusDto.Disqualified}>
            <Box sx={{ mt: 2 }}>
              <SelectInput label={'Select disqualified reason'}
                           hideTick
                           value={disqualifiedReason}
                           onChange={(event: any) => {
                             setDisqualifiedReason(event.target.value as DisqualifiedReasonDto);
                           }}
              >
                <MenuItem value={DisqualifiedReasonDto.NoLongerInterestedInSwitching}>No Longer Interested In
                  Switching</MenuItem>
                <MenuItem value={DisqualifiedReasonDto.UnableToConnect}> Unable To Connect</MenuItem>
                <MenuItem value={DisqualifiedReasonDto.WentToAnotherBroker}> Went To Another Broker</MenuItem>
              </SelectInput>
            </Box>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant={'outlined'}
                       loading={loading}
                       onClick={() => setWasClosed(true)}>
          Cancel
        </LoadingButton>
        <LoadingButton variant={'contained'}
                       disabled={disableSaveButton}
                       loading={loading}
                       color={'primary'}
                       onClick={handleSaveClick}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
